import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Page from '../../../common/Page';
import SubHeader from '../../../common/SubHeader';
import MenuCard from '../../../common/MenuCard';
import Loader from '../../../common/Loader';
import EmptyState from '../../../common/EmptyState';
import { listEvents, getTicketCounts } from '../../../common/api';
import AttendeesTotal from '../../../common/AttendeesTotal';
import { groupByMonth, monthLabel, formatEventDateTime } from '../../../common/dates';
import { displayEventName, eventCardSubtitle, eventTypeLabel, isPrivateEvent, eventDetailPath, normalizeTypeFilter, filterEventsByType, eventAttendees, attendeesLabel, sumAttendees } from '../../../common/events';
import TypeFilter from '../../../common/TypeFilter';

const BASE_PATH = '/admin/events/future';

// Eventi da oggi in avanti: selezione del mese → lista eventi del mese
function FutureEvents() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [ticketCounts, setTicketCounts] = useState(null); // { eventKey: n } del mese selezionato
  const [countsLoading, setCountsLoading] = useState(false);

  useEffect(() => {
    listEvents('future')
      .then((result) => setEvents(result.events || []))
      .catch((err) => {
        console.error('Error fetching events:', err);
        setError('Errore nel caricamento degli eventi');
        toast.error('Errore nel caricamento degli eventi');
      })
      .finally(() => setIsLoading(false));
  }, []);

  const typeFilter = normalizeTypeFilter(searchParams.get('type'));
  const months = groupByMonth(filterEventsByType(events, typeFilter));
  const selectedKey = searchParams.get('month');
  const selected = selectedKey ? months.find((m) => m.key === selectedKey) : null;
  const selectedEvents = selected ? selected.events : [];

  // Biglietti HubSpot degli eventi pubblici del mese selezionato, con una sola chiamata
  useEffect(() => {
    if (!selectedKey || selectedEvents.length === 0) {
      setTicketCounts(null);
      return;
    }
    const ts = selectedEvents.map((e) => e.whenTs);
    const from = Math.min(...ts) - 1;
    const to = Math.max(...ts) + 1;
    let cancelled = false;
    setCountsLoading(true);
    getTicketCounts(from, to)
      .then((result) => { if (!cancelled) setTicketCounts(result.counts || {}); })
      .catch((err) => { console.error('Error fetching ticket counts:', err); if (!cancelled) setTicketCounts({}); })
      .finally(() => { if (!cancelled) setCountsLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, events.length]);

  if (isLoading) {
    return <Loader />;
  }

  // aggiorna i parametri URL conservando il filtro tipologia
  const setParams = (next) => {
    const params = { ...next };
    if (typeFilter !== 'all') params.type = typeFilter;
    setSearchParams(params);
  };
  const setTypeFilter = (type) => {
    const params = {};
    if (selectedKey) params.month = selectedKey;
    if (type !== 'all') params.type = type;
    setSearchParams(params);
  };

  const openEvent = (event) => {
    const back = `${BASE_PATH}?month=${selectedKey}${typeFilter !== 'all' ? `&type=${typeFilter}` : ''}`;
    navigate(eventDetailPath(event, { back }));
  };

  return (
    <Page>
      {!selectedKey ? (
        <>
          <SubHeader title="Eventi futuri" onBack={() => navigate('/admin/dashboard')} />
          <TypeFilter value={typeFilter} onChange={setTypeFilter} />
          {error && <EmptyState text={error} />}
          {!error && months.length === 0 && <EmptyState text="Nessun evento futuro" />}
          {months.map((group) => (
            <MenuCard
              key={group.key}
              title={monthLabel(group.year, group.month)}
              badge={`${group.events.length} ${group.events.length === 1 ? 'evento' : 'eventi'}`}
              onClick={() => setParams({ month: group.key })}
            />
          ))}
        </>
      ) : (
        <>
          <SubHeader
            title={selected ? monthLabel(selected.year, selected.month) : 'Eventi futuri'}
            onBack={() => setParams({})}
          />
          <TypeFilter
            value={typeFilter}
            onChange={setTypeFilter}
            right={selected && <AttendeesTotal total={sumAttendees(selectedEvents, ticketCounts)} loading={countsLoading} />}
          />
          {!selected && <EmptyState text="Nessun evento in questo mese" />}
          {selected && selected.events.map((event) => (
            <MenuCard
              key={event.eventKey}
              title={displayEventName(event.eventName)}
              subtitle={eventCardSubtitle(event, formatEventDateTime(event.when))}
              badge={eventTypeLabel(event)}
              badgeVariant={isPrivateEvent(event) ? 'muted' : 'brand'}
              meta={countsLoading && !isPrivateEvent(event) ? '…' : attendeesLabel(eventAttendees(event, ticketCounts))}
              onClick={() => openEvent(event)}
            />
          ))}
        </>
      )}
    </Page>
  );
}

export default FutureEvents;
